import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchAllFlags, updateFeatureFlag } from "@/lib/admin-api/feature-flags";
import type { FeatureFlag } from "@/lib/feature-flags/types";

const NAMESPACE_LABELS: Record<string, string> = {
  "delivery.customer": "Suivi client",
  "delivery.portal": "Portail livreur",
  "delivery.admin": "Administration livraisons",
  "delivery.analytics": "Analytique livraisons",
};

function FlagRow({
  flag,
  onToggle,
  isUpdating,
}: {
  flag: FeatureFlag;
  onToggle: (key: string, enabled: boolean) => void;
  isUpdating: boolean;
}) {
  const depCount = flag.dependencies.length;

  return (
    <div className="flex items-center justify-between py-3 px-4 border-b border-neutral-100 last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <code className="text-xs font-mono text-neutral-500">{flag.key}</code>
          {depCount > 0 && (
            <span className="text-[10px] bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded">
              {depCount} dep{depCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-700 mt-0.5">{flag.description_fr}</p>
        {flag.roles.length > 0 && (
          <p className="text-xs text-neutral-400 mt-0.5">
            Rôles : {flag.roles.join(", ")}
          </p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={flag.enabled}
        aria-label={`${flag.enabled ? "Désactiver" : "Activer"} ${flag.description_fr}`}
        disabled={isUpdating}
        onClick={() => onToggle(flag.key, !flag.enabled)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
          flag.enabled ? "bg-emerald-500" : "bg-neutral-200"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
            flag.enabled ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

export default function FlagsPage() {
  const queryClient = useQueryClient();
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-feature-flags"],
    queryFn: () => fetchAllFlags(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      updateFeatureFlag(key, {
        enabled,
        reason: enabled ? "Activé depuis le panneau admin" : "Désactivé depuis le panneau admin",
      }),
    onMutate: ({ key }) => setUpdatingKey(key),
    onSuccess: (_, { key, enabled }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-feature-flags"] });
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
      toast.success(enabled ? "Flag activé" : "Flag désactivé");
    },
    onError: () => {
      toast.error("Erreur lors de la mise à jour");
    },
    onSettled: () => setUpdatingKey(null),
  });

  const flags = data?.flags ?? [];
  const grouped = flags.reduce<Record<string, FeatureFlag[]>>((acc, flag) => {
    const ns = flag.namespace;
    if (!acc[ns]) acc[ns] = [];
    acc[ns].push(flag);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <h1 className="font-display text-2xl font-semibold text-stone-900 mb-1">
          Feature Flags — Livraisons
        </h1>
        <p className="text-sm text-stone-500 mb-8">
          Activez ou désactivez les fonctionnalités du système de livraison.
          Les flags enfants sont automatiquement désactivés quand leur parent est inactif.
        </p>

        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-20 bg-stone-100 rounded-lg animate-pulse"
              />
            ))}
          </div>
        )}

        {!isLoading &&
          Object.entries(grouped).map(([namespace, nsFlags]) => (
            <section key={namespace} className="mb-8">
              <h2 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-2">
                {NAMESPACE_LABELS[namespace] ?? namespace}
              </h2>
              <div className="bg-white rounded-lg border border-stone-200 divide-y divide-stone-100">
                {nsFlags.map((flag) => (
                  <FlagRow
                    key={flag.key}
                    flag={flag}
                    isUpdating={updatingKey === flag.key}
                    onToggle={(key, enabled) =>
                      toggleMutation.mutate({ key, enabled })
                    }
                  />
                ))}
              </div>
            </section>
          ))}
      </div>
    </AdminLayout>
  );
}
